import moment from "moment";
import { useMemo, useState } from "react";

const inputClass =
  "w-full h-8 px-2 text-sm text-center border border-[#DBDBDB] rounded-lg bg-white text-[#0F172A] placeholder:text-[#A4B1C1] focus:outline-none focus:border-[#94A3B8]";

const presets = [
  { label: "30 min", day: 0, hour: 0, min: 30 },
  { label: "1 hour", day: 0, hour: 1, min: 0 },
  { label: "3 hours", day: 0, hour: 3, min: 0 },
  { label: "1 day", day: 1, hour: 0, min: 0 },
];

export default function ExpiryInput({ callback }) {
  const [data, setData] = useState({
    date: moment().add(3, "hours"),
    day: 0,
    hour: 3,
    min: 0,
  });

  const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

  const toIntStrict = (v) => {
    if (typeof v === "number") return Number.isFinite(v) ? Math.floor(v) : null;
    if (typeof v !== "string") return null;
    const s = v.trim();
    if (!/^\d+$/.test(s)) return null;
    return parseInt(s, 10);
  };

  function handleChange(obj) {
    const nextDayRaw = toIntStrict(obj?.day);
    const nextHourRaw = toIntStrict(obj?.hour);
    const nextMinRaw = toIntStrict(obj?.min);

    const day = nextDayRaw === null ? data.day : clamp(nextDayRaw, 0, 14);
    const hour = nextHourRaw === null ? data.hour : clamp(nextHourRaw, 0, 23);
    const min = nextMinRaw === null ? data.min : clamp(nextMinRaw, 0, 59);

    const newData = {
      ...data,
      ...obj,
      day,
      hour,
      min,
      date: moment().add(day, "days").add(hour, "hours").add(min, "minutes"),
    };

    setData(newData);
    callback && callback(newData?.date);
  }

  const formatted = useMemo(
    () => moment(data?.date).format("ddd, MMM D HH:mm"),
    [data?.date],
  );

  const isActivePreset = (p) =>
    Number(data.day) === p.day &&
    Number(data.hour) === p.hour &&
    Number(data.min) === p.min;

  const fields = [
    { key: "day", label: "Days", max: "14", value: data.day },
    { key: "hour", label: "Hours", max: "23", value: data.hour },
    { key: "min", label: "Mins", max: "59", value: data.min },
  ];

  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
            Payment expiry
          </div>
          <div className="text-[11px] text-[#6D7C94] truncate">
            How long this quote stays valid
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[10px] text-[#94A3B8]">Expires</div>
          <div className="text-xs font-semibold text-red-600 bg-red-50 rounded-md px-2 py-0.5">
            {formatted}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        {fields.map((f) => (
          <div key={f.key} className="min-w-0">
            <div className="flex items-center justify-between mb-0.5 px-0.5">
              <span className="text-[10px] font-medium text-[#6D7C94]">
                {f.label}
              </span>
              <span className="text-[9px] text-[#94A3B8]">0–{f.max}</span>
            </div>
            <input
              type="text"
              inputMode="numeric"
              className={inputClass}
              placeholder="0"
              value={String(f.value ?? "")}
              onChange={(e) => handleChange({ [f.key]: e.target.value })}
            />
          </div>
        ))}
      </div>

      <div className="mt-1.5 flex flex-wrap gap-1">
        {presets.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => handleChange(p)}
            className={`h-7 rounded-lg px-2.5 text-[11px] font-semibold border transition
              ${
                isActivePreset(p)
                  ? "bg-primary1 text-white border-primary1"
                  : "bg-white text-[#0F172A] border-[#E2E8F0] hover:bg-[#F8FAFC]"
              }`}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}
